/**
 * Where a demo request came from, so a lead can be answered with "they
 * clicked the trial ad" rather than a shrug.
 *
 * Before this the form recorded who somebody was and nothing about how they
 * found us, and with Meta ads running nobody could say which leads they
 * bought. Read on the page somebody lands on, because that is the only
 * moment the ad's query string and the outside referrer are there: the
 * first click drops the query string, and by the time somebody reaches /demo
 * the page that sent them is long gone.
 *
 * Kept in sessionStorage for the visit, like lib/adOffer.js. A page with
 * something to say (a tag, a click id, an outside referrer) replaces what is
 * kept: somebody who browsed the site and then came back through an ad in
 * the same tab came from the ad. A plain page inside the site never does,
 * since nothing on the site links with those parameters. It is posted only
 * with a demo request, never on its own.
 */

const KEY = "fdn_lead_source";
const PARAMS = ["utm_source", "utm_medium", "utm_campaign", "fbclid", "gclid", "offer"];
// The backend trims too. This only keeps a 1KB fbclid out of the request.
const MAX = 300;

const clip = (v) => String(v || "").trim().slice(0, MAX);

function fromThisPage() {
  const q = new URLSearchParams(window.location.search);
  const seen = {};
  PARAMS.forEach((p) => { seen[p] = clip(q.get(p)); });
  // Our own pages are not a source: a reload or a full-page link inside the
  // site would otherwise say the lead "came from" foundationagent.co.uk.
  let referrer = "";
  try {
    const ref = document.referrer ? new URL(document.referrer) : null;
    // Stripe sends people back after checkout; that is not how they found us.
    if (ref && ref.host !== window.location.host && !ref.host.endsWith("stripe.com")) {
      referrer = clip(ref.href);
    }
  } catch { /* an unparseable referrer is no referrer */ }
  seen.referrer = referrer;
  seen.landing_path = clip(window.location.pathname);
  return seen;
}

const saysSomething = (seen) => [...PARAMS, "referrer"].some((k) => seen[k]);

/** Call once at boot, on every full page load. */
export function captureLeadSource() {
  try {
    const seen = fromThisPage();
    if (saysSomething(seen) || !window.sessionStorage.getItem(KEY)) {
      window.sessionStorage.setItem(KEY, JSON.stringify(seen));
    }
  } catch { /* private mode, or no window: leadSource() reads the page instead */ }
}

/** The fields to post with a demo request. Never throws, never undefined. */
export function leadSource() {
  let kept = null;
  try { kept = JSON.parse(window.sessionStorage.getItem(KEY) || "null"); } catch { /* blocked */ }
  let src = kept;
  if (!src || typeof src !== "object") {
    try { src = fromThisPage(); } catch { src = {}; }
  }
  const out = {};
  [...PARAMS, "referrer", "landing_path"].forEach((k) => { out[k] = clip(src[k]); });
  return out;
}
